import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';

import { SYS4120_R01Dto } from './Dto_SYS4120_R01';
import { useSYS4120_R01Store } from './Store_SYS4120_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'SYS4120_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS4120_R01Dto> | null>;
	defaultSYS4120_R01Row: SYS4120_R01Dto;
}

export const Table_SYS4120_R01 = (props: Props) => {
	const { gridRef, defaultSYS4120_R01Row } = props;

	const {
		SYS4120_R01List,
		selectedSYS4120_R01,
		setSelectedSYS4120_R01,
		initSelectedSYS4120_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useSYS4120_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'noticeNo',
		gridData: SYS4120_R01List,
		defaultGridData: defaultSYS4120_R01Row,
		selectedRow: selectedSYS4120_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		writerDt: '게시일자',
		noticeNo: 'No',
		noticeTitle: '제목',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'noticeNo')(params), []);

	const baseColumnDefs: ColDef<SYS4120_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '게시일자', colId: 'writerDt', field: 'writerDt', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'writerDt'),
			valueFormatter: (p) => p.data?.writerDt ? formatDate(new Date(p.data.writerDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '제목', colId: 'noticeTitle', field: 'noticeTitle', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'noticeTitle'),
		},
		{
			headerName: 'No', colId: 'noticeNo', field: 'noticeNo', width: 130,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'noticeNo'),
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SYS4120_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<SYS4120_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<SYS4120_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedSYS4120_R01?.noticeNo === rowData.noticeNo;
		setSelectedSYS4120_R01(isSelected ? undefined : rowData);
	}, [selectedSYS4120_R01, setSelectedSYS4120_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		noticeNo: `${SYS4120_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [SYS4120_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [SYS4120_R01List, selectedSYS4120_R01]);

	return (
		<GridContextWrapper<SYS4120_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSYS4120_R01}
			defaultRowData={defaultSYS4120_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SYS4120_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SYS4120_R01Dto>({
					idKey: 'noticeNo',
					selectedList: selectedSYS4120_R01 ? [selectedSYS4120_R01.noticeNo] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
